/**
 * HelpLicencePage — read-only summary of the tenant's commercial
 * ceiling and the user's current access profile. Reuses the existing
 * /api/tenants/current/plan response (already fetched into
 * FeatureContext) and the AuthContext role/profile data — no new
 * backend endpoint is added.
 *
 * Visible to all authenticated users. The body intentionally exposes
 * only feature *availability* (allowed / unavailable / limited) — it
 * never shows pricing, billing internals, provider readiness, or
 * cross-tenant information.
 */
import React from 'react';
import AppHeader from '../layout/AppHeader';
import { useAuth } from '../../context/AuthContext';
import { useFeatures } from '../../context/FeatureContext';

const PROFILE_LABELS = {
  viewer: 'Viewer',
  operator: 'Operator',
  power_user: 'Power User',
  admin: 'Admin',
  owner: 'Owner',
};

const PRIMARY_FEATURE_ORDER = [
  { key: 'scout',                   label: 'Scout — daily scans' },
  { key: 'fixer',                   label: 'Fix — remediation roadmaps' },
  { key: 'fit',                     label: 'Fit — engagement suitability' },
  { key: 'pipeline_dashboard',      label: 'Pipeline dashboard' },
  { key: 'sector_tracker',          label: 'Sector Tracker' },
  { key: 'webhooks',                label: 'Webhooks & alerts' },
  { key: 'bulk_export',             label: 'Bulk export' },
  { key: 'audit_log',               label: 'Audit log' },
  { key: 'api_access',              label: 'Programmatic API access' },
];

function ValueChip({ value }) {
  const tone = value === true
    ? { bg: '#dcfce7', color: '#166534', label: 'Allowed' }
    : value === 'limited'
      ? { bg: '#fef3c7', color: '#92400e', label: 'Limited' }
      : { bg: '#f3f4f6', color: '#475569', label: 'Unavailable' };
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600,
      background: tone.bg, color: tone.color, textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>{tone.label}</span>
  );
}

export default function HelpLicencePage() {
  const { user } = useAuth();
  const { ready, planId, planLabel, features, activationLabel, daysRemaining } = useFeatures();

  const profile = user?.is_platform_root ? 'platform_root' : (user?.user_access_profile || null);
  const profileLabel = profile === 'platform_root'
    ? 'Platform Root'
    : (PROFILE_LABELS[profile] || (user?.role_label || '—'));

  const failed = ready && !planId;

  return (
    <>
      <AppHeader active="help" />
      <div className="page-shell" data-testid="help-licence-page">
        <div className="qf-glass-panel" style={{ padding: 24, marginTop: 12, maxWidth: 720 }}>
          <h2 style={{ marginTop: 0, fontSize: 22, fontWeight: 700 }}>Licence &amp; Plan</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            <strong>Tenant plan controls availability.</strong> User access controls
            what this person can use inside the tenant allowance.
          </p>

          {!ready && (
            <div data-testid="help-licence-loading" style={{ fontSize: 13, marginTop: 12 }}>
              Loading licence information…
            </div>
          )}

          {failed && (
            <div className="qf-md-quote" data-testid="help-licence-error" style={{ marginTop: 12 }}>
              Licence information could not be loaded. Please try again shortly.
            </div>
          )}

          {ready && !failed && (
            <>
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>Tenant licence</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Current plan</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }} data-testid="licence-plan-label">
                    {planLabel || planId}
                  </span>
                </div>
                {activationLabel && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Activation</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }} data-testid="licence-activation-label">
                      {activationLabel}
                      {typeof daysRemaining === 'number' && ` · ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`}
                    </span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>Plan features</h3>
                <div data-testid="licence-features-list" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 4 }}>
                  {PRIMARY_FEATURE_ORDER.map((f) => (
                    <div key={f.key}
                         data-testid={`licence-feature-${f.key}`}
                         style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px dashed rgba(148,163,184,0.18)' }}>
                      <span style={{ fontSize: 13 }}>{f.label}</span>
                      <ValueChip value={features?.[f.key]} />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>Your access profile</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Role</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }} data-testid="licence-role-label">
                    {user?.role_label || '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Access profile</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }} data-testid="licence-profile-label">
                    {profileLabel}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, marginBottom: 0 }}>
                  Owners and Admins can change a user&apos;s access from the Users page.
                  Reserved security features remain role-controlled.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
